import { call, put, select, take } from 'redux-saga/effects';
import { updateCabinetSuccess } from 'containers/Configurator/actions/furniture';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import {
  FurnitureEditor,
  FurnitureEditorContext,
} from 'lib/constraintsChecker/FurnitureEditor';
import { makeSelectRules } from 'containers/Configurator/selectors/rules';
import {
  makeSelectContext,
  makeSelectIsActiveCustomMode,
} from 'containers/Configurator/selectors/general';
import { Message } from 'containers/InteractiveArea/Message';
import { addMessageAction } from 'containers/InteractiveArea/actions';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { makeSelectLastHistoryFurniture } from 'containers/Configurator/selectors/history';
import { makeSelectShelvesLevelSelected } from 'containers/Configurator/selectors/selection';
import { makeSelectSelectedIdCabinet } from 'containers/Configurator/selectors/furniture';
import * as Sentry from '@sentry/browser';
import { Furniture } from 'lib/modelBuilder/entities/Furniture';
import { checkAndUpdate } from '../../../lib/modelBuilder/builder/change';
import { switchModeAction } from '../../ModeBar/actions';
import { ConfiguratorMode } from '../../ModeBar/reducer';
import ActionModeTypes from '../../ModeBar/constants';
import ActionPaintTypes from '../../PaintPanel/constants';
import { makeSelectConfiguratorMode } from '../../ModeBar/selectors';

export const changeSaga = (request: any, saga: any, failure?: any) =>
  function*() {
    while (true) {
      const { payload } = yield take(request);
      try {
        const rules = yield select(makeSelectRules());
        let furniture: Furniture | null = yield select(
          makeSelectLastHistoryFurniture(),
        );
        if (!furniture) return;
        const selectedIdCabinet: string | null = yield select(
          makeSelectSelectedIdCabinet(),
        );

        const context: FurnitureEditorContext = yield select(
          makeSelectContext(),
        );
        const customMode: boolean = yield select(
          makeSelectIsActiveCustomMode(),
        );
        let furnitureNext = furniture.clone();
        let cabinetOrigin: Cabinet | null = null;
        let cabinetNext: Cabinet | null = null;
        let selectedShelves: Shelf[] = [];
        const shelvesLevelSelected: number[] = yield select(
          makeSelectShelvesLevelSelected(),
        );

        if (selectedIdCabinet) {
          cabinetOrigin = furniture.get(selectedIdCabinet);
          cabinetNext = furnitureNext.get(selectedIdCabinet);

          if (!cabinetNext)
            throw new Error('No cabinet selected found in the furniture');

          if (shelvesLevelSelected.length === 0)
            selectedShelves = cabinetNext.shelves;
          else
            selectedShelves = cabinetNext.shelves.filter(item =>
              shelvesLevelSelected.includes(item.level),
            );
        }

        let isNotPreview = true;
        if (
          payload &&
          payload.preview !== undefined &&
          payload.preview !== null &&
          payload.preview === true
        ) {
          isNotPreview = false;
        }
        const mode: ConfiguratorMode = yield select(
          makeSelectConfiguratorMode(),
        );
        if (
          request !== ActionModeTypes.APPLY_COLOR_HANDLE &&
          request !== ActionModeTypes.APPLY_DELETE &&
          request !== ActionPaintTypes.APPLY_PAINT_COLOR &&
          mode !== ConfiguratorMode.selector &&
          isNotPreview
        ) {
          yield put(switchModeAction(ConfiguratorMode.selector));
        }

        const ret = yield call(
          saga,
          cabinetOrigin,
          cabinetNext,
          payload,
          selectedShelves,
          furniture,
          furnitureNext,
        );

        const {
          ignore,
          postSuccess,
          newCabinetOrigin,
          newCabinetNext,
          newFurnitureOrigin,
          newFurnitureNext,
        } = ret;

        if (ignore) continue;
        if (newCabinetOrigin) cabinetOrigin = newCabinetOrigin;
        if (newCabinetNext) cabinetNext = newCabinetNext;
        if (newFurnitureOrigin) furniture = newFurnitureOrigin;
        if (furniture && cabinetOrigin && selectedIdCabinet)
          furniture.replace(cabinetOrigin, selectedIdCabinet);
        if (newFurnitureNext) furnitureNext = newFurnitureNext;

        if (cabinetNext) checkAndUpdate(cabinetNext);

        if (!cabinetOrigin) {
          if (!furniture || !furniture.first) {
            throw new Error('No furniture or cabinet to apply change');
          }
          cabinetOrigin = cabinetNext = furniture.first;
        }
        if (!furniture || !cabinetNext) {
          throw new Error('No furniture or cabinet to apply change');
        }

        const furnitureEditor = new FurnitureEditor(
          furniture,
          selectedIdCabinet,
          rules,
          context,
          customMode,
        );

        furnitureEditor.checkRules(cabinetNext);

        let message: Message | undefined = undefined;

        if (furnitureEditor.message && isNotPreview) {
          message = Message.getMessageFromCabinetEditor(
            furnitureEditor,
            cabinetOrigin,
          );
          if (message) yield put(addMessageAction(message));
        }

        if (furnitureEditor.cabinet !== undefined) {
          checkAndUpdate(furnitureEditor.cabinet);

          if (furniture) {
            // Remove the image if any because user have modified the cabinet
            furnitureEditor.cabinet.image = undefined;
            furnitureEditor.cabinet.reference = undefined;
            if (selectedIdCabinet)
              furnitureNext.replace(furnitureEditor.cabinet, selectedIdCabinet);
            else if (cabinetNext)
              furnitureNext.replace(furnitureEditor.cabinet, cabinetNext.id);

            yield put(
              updateCabinetSuccess(
                furnitureNext,
                selectedIdCabinet,
                !isNotPreview,
                message,
              ),
            );
            if (postSuccess) yield call(postSuccess, cabinetNext, payload);
          }
        }
      } catch (error) {
        if (failure) yield put(failure(error.message));
        Sentry.captureException(error);
        throw error;
      }
    }
  };
